import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ModalService } from '@fino-ui/modal';
import { TranslationModule } from '@fino-ui/translation';
import { KeycloakService } from 'keycloak-angular';
import { AppService } from 'src/app/app.service';
import {
  FileUploadModalComponent,
  UPLOAD_TYPES,
} from 'src/app/components/file-upload-modal/file-upload-modal.component';
import { CachingService } from 'src/app/core/common/caching.service';
import { ActionBoxComponent } from '../../components/action-box/action-box.component';
import { BLZPipe } from '../../core/pipes/blz.pipe';
import { AdminUsersComponent } from './components/admin-users/admin-users.component';
import { BlzFusionComponent } from './components/blz-fusion/blz-fusion.component';
import type { OverviewConfig } from './overview.model';
import { OverviewService } from './overview.service';

export const OVERVIEW_MODEL = 'overviewSettings';

@Component({
  selector: 'app-overview',
  templateUrl: './overview.component.html',
  styleUrl: './overview.component.sass',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ActionBoxComponent,
    TranslationModule,
    AdminUsersComponent,
    BLZPipe,
  ],
})
export class OverviewComponent {
  constructor(
    private overviewService: OverviewService,
    private appService: AppService,
    private ngxModalService: ModalService,
    private route: ActivatedRoute,
    private router: Router,
    private authService: KeycloakService,
    private cachingService: CachingService,
  ) {}

  tenantSettings: OverviewConfig;
  userRoles: string[] = [];

  get logoUrl() {
    return this.appService.logoUrl;
  }

  get hasKWH() {
    return this.appService.availableProducts().hasKwh;
  }

  get showFusionSection() {
    return this.appService.isAtruvia && this.hasKWH;
  }

  hasRole(role: string) {
    return this.userRoles.includes(role);
  }

  ngOnInit(): void {
    this.userRoles = this.authService.getUserRoles();

    this.overviewService.fetchOverview().subscribe((resp: OverviewConfig) => {
      this.cacheDataModel(resp);

      this.route.queryParams.subscribe((p) => {
        if (p.modal) {
          this[p.modal]();
          // remove queryparams silently
          this.router.navigate([], {});
        }
      });
    });
  }

  cacheDataModel(resp: OverviewConfig, overwrite = false) {
    this.tenantSettings = this.cachingService.cacheDataModel(
      resp,
      OVERVIEW_MODEL,
      overwrite,
    );
  }

  openLogoModal() {
    this.ngxModalService
      .addModal<string>(FileUploadModalComponent, {
        title: 'home.logo-upload.title',
        description: 'home.logo-upload.description',
        fileType: UPLOAD_TYPES.IMAGE,
      })
      .subscribe((result) => {
        if (result) {
          this.overviewService.uploadLogo(result).subscribe({
            next: () => {
              this.appService.success('overview.actions.logo.success');
              this.cacheDataModel(
                { ...this.tenantSettings, logoUrl: result },
                true,
              );
              this.appService.logoUrl.set(result);
            },
            error: () => {
              this.appService.error('overview.actions.logo.error');
            },
          });
        }
      });
  }

  openFusionModal() {
    this.ngxModalService
      .addModal<string[]>(BlzFusionComponent, {
        deprecatedBankCodes: this.tenantSettings.deprecatedBankCodes,
      })
      .subscribe((result) => {
        if (result) {
          this.overviewService.deprecatedBankCodes(result).subscribe({
            next: () => {
              this.appService.success('overview.actions.fusion.success', {
                number: result.length,
              });
            },
            error: () => {
              this.appService.error('overview.actions.fusion.error');
            },
          });
        }
      });
  }
}
